import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { SavedWorldBuilding } from '@creanizer/types'
import { createIndexedDBStorage } from '@creanizer/utils'
import { generateId } from '../utils/generateId'

interface WorldBuilderStore {
  savedWorlds: SavedWorldBuilding[]
  currentWorld: SavedWorldBuilding | null
  maxWorlds: number
  
  // CRUD操作
  addWorld: (world: Omit<SavedWorldBuilding, 'id' | 'createdAt' | 'updatedAt'>) => void
  updateWorld: (id: string, updates: Partial<SavedWorldBuilding>) => void
  deleteWorld: (id: string) => void
  getWorld: (id: string) => SavedWorldBuilding | undefined
  clearWorlds: () => void
  
  // 現在の世界観
  setCurrentWorld: (world: SavedWorldBuilding | null) => void
  
  // エクスポート/インポート
  exportAsText: () => void
  exportAsJSON: () => string
  exportSingleAsText: (id: string) => void
  exportSingleAsJSON: (id: string) => void
  importFromJSON: (jsonString: string) => void
  
  // 検索・フィルタ
  searchWorlds: (query: string) => SavedWorldBuilding[]
  getWorldsByTag: (tag: string) => SavedWorldBuilding[]
}

export const useWorldBuilderStore = create<WorldBuilderStore>()(
  persist(
    (set, get) => ({
      savedWorlds: [],
      currentWorld: null,
      maxWorlds: 500,
      
      addWorld: (world) => {
        const state = get()
        if (state.savedWorlds.length >= state.maxWorlds) {
          throw new Error(`保存できる世界観の上限（${state.maxWorlds}件）に達しています`)
        }
        
        const newWorld: SavedWorldBuilding = {
          ...world,
          id: generateId('wb'),
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        }
        
        set((state) => ({
          savedWorlds: [...state.savedWorlds, newWorld]
        }))
      },
      
      updateWorld: (id, updates) => {
        set((state) => ({
          savedWorlds: state.savedWorlds.map((world) =>
            world.id === id
              ? { ...world, ...updates, updatedAt: new Date().toISOString() }
              : world
          ),
        }))
      },
      
      deleteWorld: (id) => {
        set((state) => ({
          savedWorlds: state.savedWorlds.filter((world) => world.id !== id),
          currentWorld: state.currentWorld?.id === id ? null : state.currentWorld,
        }))
      },
      
      getWorld: (id) => {
        return get().savedWorlds.find((world) => world.id === id)
      },
      
      clearWorlds: () => {
        set({ savedWorlds: [], currentWorld: null })
      },
      
      setCurrentWorld: (world) => {
        set({ currentWorld: world })
      },
      
      exportAsText: () => {
        const { savedWorlds } = get()
        if (savedWorlds.length === 0) {
          alert('保存された世界観がありません。')
          return
        }
        const text = formatWorldForExport(savedWorlds)
        
        const blob = new Blob([text], { type: 'text/plain;charset=utf-8' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `saved_worlds_${Date.now()}.txt`
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        URL.revokeObjectURL(url)
      },
      
      exportAsJSON: () => {
        const { savedWorlds } = get()
        if (savedWorlds.length === 0) {
          alert('保存された世界観がありません。')
          return JSON.stringify([])
        }
        
        const data = {
          version: '1.0',
          type: 'worlds',
          exportDate: new Date().toISOString(),
          count: savedWorlds.length,
          data: savedWorlds,
        }
        
        return JSON.stringify(data, null, 2)
      },
      
      exportSingleAsText: (id: string) => {
        const world = get().getWorld(id)
        if (!world) {
          alert('世界観が見つかりません。')
          return
        }
        
        const text = formatWorldForExport([world])
        const blob = new Blob([text], { type: 'text/plain;charset=utf-8' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `world_${world.result.name}_${Date.now()}.txt`
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        URL.revokeObjectURL(url)
      },
      
      exportSingleAsJSON: (id: string) => {
        const world = get().getWorld(id)
        if (!world) {
          alert('世界観が見つかりません。')
          return
        }
        
        const exportData = {
          version: '1.0',
          type: 'world',
          exportDate: new Date().toISOString(),
          data: world
        }
        
        const json = JSON.stringify(exportData, null, 2)
        const blob = new Blob([json], { type: 'application/json;charset=utf-8' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `world_${world.result.name}_${Date.now()}.json`
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        URL.revokeObjectURL(url)
      },
      
      importFromJSON: (jsonString) => {
        try {
          const imported = JSON.parse(jsonString)
          
          // 複数の形式をサポート
          let worlds: SavedWorldBuilding[]
          if (imported.type === 'world' && imported.data && !Array.isArray(imported.data)) {
            // 単独世界観の場合
            worlds = [imported.data as SavedWorldBuilding]
          } else {
            // 複数世界観の場合
            worlds = (
              imported.data?.worlds ||  // VersionManager変換後の形式
              (imported.type === 'worlds' ? imported.data : null) ||  // 個別エクスポート形式
              imported.data ||  // BulkDataManager形式
              imported.worlds ||  // 既存形式
              (Array.isArray(imported) ? imported : [])  // 直接配列形式
            ) as SavedWorldBuilding[]
          }
          
          const currentWorlds = get().savedWorlds
          const validWorlds = worlds.filter(world => 
            world.result && world.result.name
          )
          
          // 重複チェック（名前で判定）
          const uniqueWorlds = validWorlds.filter(newWorld => {
            return !currentWorlds.some(existing => 
              existing.result.name === newWorld.result.name &&
              existing.result.concept === newWorld.result.concept
            )
          })
          
          const newWorlds: SavedWorldBuilding[] = uniqueWorlds.map((world, index) => ({
            ...world,
            id: world.id || `${Date.now() + index}_${Math.random().toString(36).slice(2, 11)}`,
            createdAt: world.createdAt || new Date().toISOString(),
            updatedAt: new Date().toISOString()
          }))
          
          set(state => {
            let worlds = [...newWorlds, ...state.savedWorlds]
            if (worlds.length > get().maxWorlds) {
              worlds = worlds.slice(0, get().maxWorlds)
            }
            return { savedWorlds: worlds }
          })
          
          const skippedCount = validWorlds.length - uniqueWorlds.length
          console.log(`Imported ${uniqueWorlds.length} unique worlds (${skippedCount} duplicates skipped)`)
        } catch (error) {
          console.error('Failed to import worlds:', error)
          throw error
        }
      },
      
      searchWorlds: (query: string) => {
        const { savedWorlds } = get()
        const lowerQuery = query.toLowerCase()
        
        return savedWorlds.filter((world) => 
          world.title.toLowerCase().includes(lowerQuery) ||
          world.result.name.toLowerCase().includes(lowerQuery) ||
          world.result.concept.toLowerCase().includes(lowerQuery) ||
          world.memo?.toLowerCase().includes(lowerQuery) ||
          world.tags?.some(tag => tag.toLowerCase().includes(lowerQuery))
        )
      },
      
      getWorldsByTag: (tag: string) => {
        const { savedWorlds } = get()
        return savedWorlds.filter((world) => 
          world.tags?.includes(tag)
        )
      },
    }),
    {
      name: 'worldbuilder-storage',
      version: 1,
      storage: createIndexedDBStorage('worldbuilder-storage'),
      partialize: (state) => ({ savedWorlds: state.savedWorlds, currentWorld: state.currentWorld, maxWorlds: state.maxWorlds }),
      migrate: (persistedState: any, version: number) => {
        // バージョン0（初期状態）からバージョン1への移行
        if (version === 0) {
          // 古いデータ構造から新しいデータ構造への変換
          return persistedState
        }
        return persistedState
      }
    }
  )
)

// 世界観をテキスト形式にフォーマット
function formatWorldForExport(worlds: SavedWorldBuilding[]): string {
  const date = new Date().toLocaleString('ja-JP')
  
  let text = '=== 世界観リスト ===\n'
  text += `エクスポート日時: ${date}\n`
  text += `合計: ${worlds.length}件\n`
  text += '='.repeat(50) + '\n\n'
  
  return text + worlds.map((world, index) => {
    const result = world.result
    const lines: string[] = []
    
    // ヘッダー
    lines.push(`[世界観${index + 1}] ${world.title}`)
    lines.push('━'.repeat(50))
    lines.push(`生成日: ${new Date(world.createdAt).toLocaleString('ja-JP')}`)
    lines.push('')
    
    // 基本情報
    lines.push('■ 基本情報')
    lines.push(`世界名: ${result.name}`)
    lines.push(`コンセプト: ${result.concept}`)
    lines.push(`概要: ${result.summary}`)
    lines.push(`ジャンル: ${result.genres.join('、')}`)
    lines.push(`世界の規模: ${result.worldScale}`)
    lines.push('')
          
          // パラメータ（全項目）
          lines.push('■ 世界観パラメータ')
          lines.push(`魔法レベル: ${result.parameters.magicLevel}/10`)
          lines.push(`科学技術レベル: ${result.parameters.scienceLevel}/10`)
          lines.push(`文明レベル: ${result.parameters.civilizationLevel}/10`)
          lines.push(`危険度: ${result.parameters.dangerLevel}/10`)
          lines.push(`神秘性: ${result.parameters.mysteryLevel}/10`)
          lines.push(`複雑さ: ${result.parameters.complexityLevel}/10`)
          if (result.parameters.uniquenessLevel) {
            lines.push(`独自性: ${result.parameters.uniquenessLevel}/10`)
          }
          if (result.parameters.chaosLevel) {
            lines.push(`混沌度: ${result.parameters.chaosLevel}/10`)
          }
          lines.push('')
          
          // 独自要素
          if (result.uniqueFeatures) {
            lines.push('■ 独自要素')
            lines.push(`シグネチャー: ${result.uniqueFeatures.signature}`)
            if (result.uniqueFeatures.laws && result.uniqueFeatures.laws.length > 0) {
              lines.push('独自法則:')
              result.uniqueFeatures.laws.forEach(law => lines.push(`  ・${law}`))
            }
            if (result.uniqueFeatures.paradoxes && result.uniqueFeatures.paradoxes.length > 0) {
              lines.push('パラドックス:')
              result.uniqueFeatures.paradoxes.forEach(p => lines.push(`  ・${p}`))
            }
            if (result.uniqueFeatures.mysteries && result.uniqueFeatures.mysteries.length > 0) {
              lines.push('謎:')
              result.uniqueFeatures.mysteries.forEach(m => lines.push(`  ・${m}`))
            }
            if (result.uniqueFeatures.innovations && result.uniqueFeatures.innovations.length > 0) {
              lines.push('革新:')
              result.uniqueFeatures.innovations.forEach(i => lines.push(`  ・${i}`))
            }
            lines.push('')
          }
          
          // 詳細情報がある場合
          if (result.details) {
            // 時代設定
            if (result.details.era) {
              lines.push('■ 時代設定')
              lines.push(`時代名: ${result.details.era.name}`)
              lines.push(`説明: ${result.details.era.description}`)
              lines.push(`技術レベル: ${result.details.era.technologyLevel}`)
              lines.push(`社会構造: ${result.details.era.socialStructure}`)
              lines.push('')
            }
            
            // 地理
            if (result.details.geography) {
              lines.push('■ 地理')
              lines.push(`概要: ${result.details.geography.description}`)
              if ((result.details.geography as any).worldSize) {
                lines.push(`世界の規模: ${(result.details.geography as any).worldSize}`)
              }
              lines.push(`気候: ${result.details.geography.climate}`)
              if (result.details.geography.terrain && result.details.geography.terrain.length > 0) {
                lines.push('地形:')
                result.details.geography.terrain.forEach(t => {
                  if (typeof t === 'object' && t !== null) {
                    lines.push(`  ・${(t as any).name}: ${(t as any).description}`)
                  } else {
                    lines.push(`  ・${t}`)
                  }
                })
              }
              if (result.details.geography.naturalResources && result.details.geography.naturalResources.length > 0) {
                lines.push('天然資源:')
                result.details.geography.naturalResources.forEach(r => {
                  if (typeof r === 'object' && r !== null) {
                    lines.push(`  ・${(r as any).name}: ${(r as any).description}`)
                  } else {
                    lines.push(`  ・${r}`)
                  }
                })
              }
              if (result.details.geography.notableLocations && result.details.geography.notableLocations.length > 0) {
                lines.push('重要な場所:')
                result.details.geography.notableLocations.forEach(l => {
                  if (typeof l === 'object' && l !== null) {
                    lines.push(`  ・${(l as any).name}: ${(l as any).description}`)
                  } else {
                    lines.push(`  ・${l}`)
                  }
                })
              }
              lines.push('')
            }
            
            // 脅威と危険
            if (result.details.threats) {
              lines.push('■ 脅威と危険')
              
              if (result.details.threats.existsHostileForce && result.details.threats.hostileForces) {
                lines.push('【敵性存在】')
                result.details.threats.hostileForces.forEach(force => {
                  lines.push(`◆ ${force.name}（${force.type}）`)
                  lines.push(`  説明: ${force.description}`)
                  lines.push(`  脅威: ${force.threat}`)
                  lines.push(`  能力: ${force.capabilities.join('、')}`)
                  if (force.weaknesses && force.weaknesses.length > 0) {
                    lines.push(`  弱点: ${force.weaknesses.join('、')}`)
                  }
                  if (force.samples && force.samples.length > 0) {
                    lines.push(`  個体例:`)
                    force.samples.forEach(s => lines.push(`    ・${s}`))
                  }
                })
                lines.push('')
              }
              
              if (result.details.threats.conflictSituation) {
                if (result.details.threats.conflictSituation.currentWars && result.details.threats.conflictSituation.currentWars.length > 0) {
                  lines.push('【現在の戦争】')
                  result.details.threats.conflictSituation.currentWars.forEach(war => {
                    lines.push(`◆ ${war.name}`)
                    lines.push(`  参加勢力: ${war.participants.join(' vs ')}`)
                    lines.push(`  原因: ${war.cause}`)
                    lines.push(`  現状: ${war.status}`)
                    lines.push(`  影響: ${war.impact}`)
                  })
                  lines.push('')
                }
                
                if (result.details.threats.conflictSituation.tensions && result.details.threats.conflictSituation.tensions.length > 0) {
                  lines.push('【緊張状態】')
                  result.details.threats.conflictSituation.tensions.forEach(t => lines.push(`  ・${t}`))
                  lines.push('')
                }
              }
              
              if (result.details.threats.worldDangers && result.details.threats.worldDangers.length > 0) {
                lines.push('【世界的な危機】')
                result.details.threats.worldDangers.forEach(d => lines.push(`  ・${d}`))
                lines.push('')
              }
            }
            
            // 文化
            if (result.details.cultures && result.details.cultures.length > 0) {
              lines.push('■ 文化')
              result.details.cultures.forEach((culture, index) => {
                lines.push(`◆ ${culture.name}`)
                lines.push(`  説明: ${culture.description}`)
                if (culture.population) lines.push(`  人口: ${culture.population}`)
                if (culture.language) lines.push(`  言語: ${culture.language}`)
                if (culture.governance) lines.push(`  統治: ${culture.governance}`)
                if (culture.architecture) lines.push(`  建築: ${culture.architecture}`)
                if (culture.clothing) lines.push(`  服装: ${culture.clothing}`)
                if (culture.values && culture.values.length > 0) {
                  lines.push(`  価値観: ${culture.values.join('、')}`)
                }
                if (culture.customs && culture.customs.length > 0) {
                  lines.push(`  習慣:`)
                  culture.customs.forEach(c => lines.push(`    ・${c}`))
                }
                if (culture.taboos && culture.taboos.length > 0) {
                  lines.push(`  タブー:`)
                  culture.taboos.forEach(t => lines.push(`    ・${t}`))
                }
                if (index < result.details.cultures.length - 1) lines.push('')
              })
              lines.push('')
            }
            
            // 歴史年表
            if (result.details.timeline && result.details.timeline.length > 0) {
              lines.push('■ 歴史年表')
              result.details.timeline.forEach(event => {
                lines.push(`◆ ${event.year}: ${event.event}`)
                if ((event as any).description) {
                  lines.push(`  説明: ${(event as any).description}`)
                }
                lines.push(`  影響度: ${event.impact}`)
                lines.push(`  カテゴリ: ${event.category}`)
                if ((event as any).consequences && (event as any).consequences.length > 0) {
                  lines.push(`  結果:`)
                  ;(event as any).consequences.forEach((c: string) => lines.push(`    ・${c}`))
                }
              })
              lines.push('')
            }
            
            // 魔法体系
            if (result.details.magicSystem && result.details.magicSystem.exists) {
              lines.push('■ 魔法体系')
              lines.push(`説明: ${result.details.magicSystem.description}`)
              if (result.details.magicSystem.rules && result.details.magicSystem.rules.length > 0) {
                lines.push('ルール:')
                result.details.magicSystem.rules.forEach(r => {
                  if (typeof r === 'object' && r !== null) {
                    lines.push(`  ・${(r as any).name}: ${(r as any).description}`)
                  } else {
                    lines.push(`  ・${r}`)
                  }
                })
              }
              if (result.details.magicSystem.limitations && result.details.magicSystem.limitations.length > 0) {
                lines.push('制限:')
                result.details.magicSystem.limitations.forEach(l => {
                  if (typeof l === 'object' && l !== null) {
                    lines.push(`  ・${(l as any).name}: ${(l as any).description}`)
                  } else {
                    lines.push(`  ・${l}`)
                  }
                })
              }
              if (result.details.magicSystem.practitioners && result.details.magicSystem.practitioners.length > 0) {
                lines.push('実践者:')
                result.details.magicSystem.practitioners.forEach(p => {
                  if (typeof p === 'object' && p !== null) {
                    lines.push(`  ・${(p as any).name}: ${(p as any).description}`)
                  } else {
                    lines.push(`  ・${p}`)
                  }
                })
              }
              lines.push('')
            }
            
            // 技術体系
            if (result.details.technology) {
              lines.push('■ 技術体系')
              lines.push(`レベル: ${result.details.technology.level}`)
              if (result.details.technology.keyTechnologies && result.details.technology.keyTechnologies.length > 0) {
                lines.push('主要技術:')
                result.details.technology.keyTechnologies.forEach(t => {
                  if (typeof t === 'object' && t !== null) {
                    lines.push(`  ・${(t as any).name}: ${(t as any).description}`)
                  } else {
                    lines.push(`  ・${t}`)
                  }
                })
              }
              if (result.details.technology.limitations && result.details.technology.limitations.length > 0) {
                lines.push('制限:')
                result.details.technology.limitations.forEach(l => {
                  if (typeof l === 'object' && l !== null) {
                    lines.push(`  ・${(l as any).name}: ${(l as any).description}`)
                  } else {
                    lines.push(`  ・${l}`)
                  }
                })
              }
              if (result.details.technology.uniqueAspects && result.details.technology.uniqueAspects.length > 0) {
                lines.push('独自の側面:')
                result.details.technology.uniqueAspects.forEach(a => {
                  if (typeof a === 'object' && a !== null) {
                    lines.push(`  ・${(a as any).name}: ${(a as any).description}`)
                  } else {
                    lines.push(`  ・${a}`)
                  }
                })
              }
              lines.push('')
            }
            
          }
          
          // 一貫性チェック
          if (result.consistency) {
            lines.push('■ 一貫性チェック')
            lines.push(`スコア: ${result.consistency.score}/100`)
            if (result.consistency.issues && result.consistency.issues.length > 0) {
              lines.push('問題点:')
              result.consistency.issues.forEach(i => lines.push(`  ・${i}`))
            }
            if (result.consistency.suggestions && result.consistency.suggestions.length > 0) {
              lines.push('改善提案:')
              result.consistency.suggestions.forEach(s => lines.push(`  ・${s}`))
            }
            lines.push('')
          }
          
          // メタデータ
          if (result.metadata) {
            lines.push('■ メタデータ')
            lines.push(`生成日時: ${result.metadata.createdAt}`)
            lines.push(`更新日時: ${result.metadata.updatedAt}`)
            lines.push(`バージョン: ${result.metadata.version}`)
            lines.push(`生成モード: ${result.metadata.generationMode}`)
            lines.push('')
          }
          
          // メモとタグ
          if (world.memo) {
            lines.push(`【メモ】`)
            lines.push(world.memo)
            lines.push('')
          }
          if (world.tags && world.tags.length > 0) {
            lines.push(`【タグ】`)
            lines.push(world.tags.join('、'))
            lines.push('')
          }
          
          lines.push('=' .repeat(50))
          lines.push('')
          
    return lines.join('\n')
  }).join('\n\n' + '='.repeat(50) + '\n\n')
}